import { clsx } from 'clsx';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Checkmark,
  Close,
  Download
} from '@teachstonellc/component-library/dist/icons';
import i18n from 'helpers/i18n';
import messages from './messages';
import RouteHelpers from 'helpers/routes';
import awardIcon from 'images/calibrations/award.svg';
import listChecklistIcon from 'images/calibrations/listChecklist.svg';
import {
  CalibrationItem,
  getCalibrationCertificateUrl
} from 'actions/calibrations';
import CalibrationTags from 'components/Calibrations/CalibrationTags';
import { getUserTimeZone, parseToDateTime } from 'helpers/date/localTimezone';

interface CalibrationHistoryCardProps {
  calibration: CalibrationItem;
}

function toLocalDateTime(value?: string | null) {
  return parseToDateTime(value)
    ?.setZone(getUserTimeZone())
    .setLocale(navigator.language);
}

interface DocumentTileContentProps {
  icon: string;
  title: string;
  subtitle: string;
  trailingIcon: React.ReactNode;
}

function DocumentTileContent({
  icon,
  title,
  subtitle,
  trailingIcon
}: DocumentTileContentProps) {
  return (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-ds-surface-card md:size-[34px]">
        <img src={icon} alt="" className="size-[18px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-[#111827] md:text-[13px]">
          {title}
        </span>
        <span className="text-xs text-[#6B7280] md:text-[11px] md:text-[#5E6574]">
          {subtitle}
        </span>
      </span>
      <span className="shrink-0 text-[#111827]">{trailingIcon}</span>
    </>
  );
}

const tileClassName =
  'flex w-full items-center gap-3 rounded-lg py-3 pl-3 pr-3.5 transition hover:brightness-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#61810D] md:w-[250px] md:rounded md:p-3.5';

function CalibrationHistoryCard({ calibration }: CalibrationHistoryCardProps) {
  const isPassed = calibration.status === 'passed';
  const showCertificate = isPassed;
  const completedAt = toLocalDateTime(calibration.completed_at);
  const scoreReportUntil = toLocalDateTime(
    calibration.score_report_available_until
  );
  const showScoreReport =
    !!scoreReportUntil && scoreReportUntil > DateTime.now();
  const showDocuments = showCertificate || showScoreReport;
  const StatusIcon = isPassed ? Checkmark : Close;

  return (
    <article className="flex flex-col overflow-hidden rounded-lg border border-ds-gray-3 bg-ds-surface-card leading-[normal] md:flex-row">
      <div
        className={clsx(
          'flex shrink-0 items-center justify-between px-4 py-3 md:w-[120px] md:flex-col md:items-start md:justify-start md:gap-2 md:px-5 md:py-6',
          isPassed ? 'bg-[#EDF6E6]' : 'bg-[#FFF0F3]'
        )}
      >
        <span
          className={clsx(
            'flex items-center gap-1.5 text-sm font-bold md:gap-1',
            isPassed ? 'text-[#4F7A2A]' : 'text-[#A02E4A]'
          )}
        >
          <StatusIcon className="size-4 md:size-[18px]" />
          {i18n.ft(isPassed ? messages.passed : messages.failed)}
        </span>
        {completedAt && (
          <p className="text-xs font-medium text-[#111827]">
            {completedAt.toLocaleString(DateTime.DATE_MED)}
            <span className="md:hidden"> · </span>
            <span className="text-[#5E6574] md:mt-2 md:block md:text-[11px] md:font-normal">
              {completedAt.toLocaleString(DateTime.TIME_SIMPLE)}
            </span>
          </p>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3.5 p-4 md:min-w-0 md:flex-row md:gap-0 md:p-0">
        <div className="flex flex-col justify-center gap-2.5 md:min-w-0 md:flex-1 md:gap-3 md:py-6 md:pl-6 md:pr-5">
          <h3 className="text-lg font-bold text-[#111827] md:text-xl">
            {calibration.name}
          </h3>
          <CalibrationTags
            classVersion={calibration.test_template.class_version}
            ageLevelGroup={calibration.test_template.age_level_group}
          />
        </div>

        {showDocuments && (
          <div className="flex shrink-0 flex-col gap-2 md:justify-center md:py-6 md:pr-6 lg:flex-row lg:items-center lg:gap-3">
            {showCertificate && (
              <a
                href={getCalibrationCertificateUrl(calibration.id)}
                className={clsx(tileClassName, 'bg-ds-goldie-2')}
              >
                <DocumentTileContent
                  icon={awardIcon}
                  title={i18n.ft(messages.certificate)}
                  subtitle={i18n.ft(messages.downloadPdf)}
                  trailingIcon={<Download className="size-4 md:size-[18px]" />}
                />
              </a>
            )}
            {showScoreReport && (
              <Link
                to={RouteHelpers.getPath('calibrations-score-report', {
                  id: calibration.id
                })}
                className={clsx(tileClassName, 'bg-ds-surface-subtle')}
              >
                <DocumentTileContent
                  icon={listChecklistIcon}
                  title={i18n.ft(messages.scoreReport)}
                  subtitle={i18n.ft(messages.availableUntil, {
                    date: scoreReportUntil.toLocaleString(DateTime.DATE_MED)
                  })}
                  trailingIcon={<ArrowRight size={16} />}
                />
              </Link>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

export default CalibrationHistoryCard;
